/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\SchedulesSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/

import { Calendar, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';

interface Schedule {
  id?: number;
  dayOfWeek: string[] | null;
  startTime: string | null;
  endTime: string | null;
  startDate: string | null;
  endDate: string | null;
}

const DAYS_OF_WEEK = [
  { value: 'mon', label: 'Monday', labelShort: 'Mon' },
  { value: 'tue', label: 'Tuesday', labelShort: 'Tue' },
  { value: 'wed', label: 'Wednesday', labelShort: 'Wed' },
  { value: 'thu', label: 'Thursday', labelShort: 'Thu' },
  { value: 'fri', label: 'Friday', labelShort: 'Fri' },
  { value: 'sat', label: 'Saturday', labelShort: 'Sat' },
  { value: 'sun', label: 'Sunday', labelShort: 'Sun' },
];

interface SchedulesSectionProps {
  schedules: Schedule[];
  errors: Record<string, string>;
  onAddSchedule: () => void;
  onRemoveSchedule: (index: number) => void;
  onUpdateSchedule: (index: number, field: keyof Schedule, value: Schedule[keyof Schedule]) => void;
  onToggleDay: (index: number, day: string) => void;
}

export function SchedulesSection({
  schedules, errors, onAddSchedule, onRemoveSchedule, onUpdateSchedule, onToggleDay
}: SchedulesSectionProps) {
  const { t } = useI18n();

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold">{t('timeGroups.sections.schedules')}</h3>
      </div>

      {errors.schedules && (
        <p className="text-sm text-red-500 mb-4">{errors.schedules}</p>
      )}

      <div className="space-y-4">
        {schedules.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">{t('timeGroups.noSchedules')}</div>
        ) : (
          schedules.map((schedule, index) => (
            <div key={`schedule-${index}`} className="rounded-lg border border-border p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
                <Label className="pt-2">
                  <SimpleTooltip content={t('timeGroups.fields.daysOfWeek.tooltip')}>
                    <span>{t('timeGroups.fields.daysOfWeek.label')}</span>
                  </SimpleTooltip>
                </Label>
                <div className="col-span-3 flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={`${index}-${day.value}`}
                        type="button"
                        onClick={() => onToggleDay(index, day.value)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          schedule.dayOfWeek?.includes(day.value)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {day.labelShort}
                      </button>
                    ))}
                  </div>
                  <SimpleTooltip content={t('timeGroups.actions.removeSchedule')}>
                    <Button type="button" variant="ghost" size="sm" onClick={() => onRemoveSchedule(index)}
                      className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </SimpleTooltip>
                </div>

                {(errors[`schedule_${index}_days`] || errors[`schedule_${index}_time`]) && (
                  <div className="col-span-3 col-start-2 -mt-2 text-sm text-red-500">
                    {errors[`schedule_${index}_days`] || errors[`schedule_${index}_time`]}
                  </div>
                )}

                <Label htmlFor={`startTime-${index}`}>
                  <SimpleTooltip content={t('timeGroups.fields.startTime.tooltip')}>
                    <span>{t('timeGroups.fields.startTime.label')}</span>
                  </SimpleTooltip>
                </Label>
                <div>
                  <Input id={`startTime-${index}`} type="time" value={schedule.startTime ?? ''}
                    onChange={(event) => onUpdateSchedule(index, 'startTime', event.target.value)} />
                </div>

                <Label htmlFor={`endTime-${index}`}>
                  <SimpleTooltip content={t('timeGroups.fields.endTime.tooltip')}>
                    <span>{t('timeGroups.fields.endTime.label')}</span>
                  </SimpleTooltip>
                </Label>
                <div>
                  <Input id={`endTime-${index}`} type="time" value={schedule.endTime ?? ''}
                    onChange={(event) => onUpdateSchedule(index, 'endTime', event.target.value)} />
                </div>

                <Label htmlFor={`startDate-${index}`}>
                  <SimpleTooltip content={t('timeGroups.fields.startDate.tooltip')}>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {t('timeGroups.fields.startDate.label')}
                    </span>
                  </SimpleTooltip>
                </Label>
                <div>
                  <Input id={`startDate-${index}`} type="date" value={schedule.startDate ?? ''}
                    onChange={(event) => onUpdateSchedule(index, 'startDate', event.target.value || null)} />
                </div>

                <Label htmlFor={`endDate-${index}`}>
                  <SimpleTooltip content={t('timeGroups.fields.endDate.tooltip')}>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {t('timeGroups.fields.endDate.label')}
                    </span>
                  </SimpleTooltip>
                </Label>
                <div>
                  <Input id={`endDate-${index}`} type="date" value={schedule.endDate ?? ''}
                    onChange={(event) => onUpdateSchedule(index, 'endDate', event.target.value || null)} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button type="button" onClick={onAddSchedule} size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          {t('timeGroups.actions.addSchedule')}
        </Button>
      </div>
    </div>
  );
}
